// Invoice attachment DAO — reads the fakturownia_attachment_candidates view
// (which file maps to which issued invoice, and whether it's already attached)
// and calls the fakturownia_mark_attached RPC (backfill historically-manual
// uploads into the ledger without re-uploading).
//
// Upload itself is NOT here — it needs Google Drive (Cloud Run) and goes
// through FakturowniaEdgeFnClient.uploadInvoiceAttachments.

import { BaseDao, type SupabaseClient, type InvalidateFn } from '../base.ts';

export interface AttachmentCandidate {
  invoice_id: number;
  external_system_id: string;
  invoice_number: string;
  transaction_id: number;
  transaction_file_id: number;
  drive_file_id: string;
  file_name: string;
  document_type: 'annex7' | 'ie599';
  border_type: 'eu' | 'non_eu';
  already_attached: boolean;
}

export class InvoiceAttachmentDao extends BaseDao<AttachmentCandidate> {
  protected readonly dependentResources = ['fakturownia_attachments', 'fakturownia_attachment_candidates'];

  constructor(sb: SupabaseClient, invalidateFn?: InvalidateFn) {
    super(sb, 'fakturownia_attachments', invalidateFn);
  }

  /** Required files (one per transaction × type), optionally scoped to one invoice.
   *  Ordered so each invoice's rows group together. */
  async listCandidates(invoiceId?: number): Promise<AttachmentCandidate[]> {
    let query = this.sb
      .from('fakturownia_attachment_candidates')
      .select('*')
      .order('invoice_number', { ascending: true })
      .order('transaction_id', { ascending: true });
    if (invoiceId != null) query = query.eq('invoice_id', invoiceId);

    const { data, error } = await query;
    if (error) throw error;
    return (data ?? []) as AttachmentCandidate[];
  }

  /** Pending candidates (already_attached = false), bounded by limit.
   *  Used by the sync_dirty loop to drive the attachment upload pass.
   *  Optional `invoiceIds` scopes the pass to selected invoices (review UI). */
  async listPendingCandidates(limit: number, invoiceIds?: number[]): Promise<AttachmentCandidate[]> {
    let query = this.sb
      .from('fakturownia_attachment_candidates')
      .select('*')
      .eq('already_attached', false);
    if (invoiceIds && invoiceIds.length > 0) query = query.in('invoice_id', invoiceIds);
    const { data, error } = await query.limit(limit);
    if (error) throw error;
    return (data ?? []) as AttachmentCandidate[];
  }

  /** Exact count of pending candidates — invoices can carry 100+ files, so
   *  dry-run summaries must count beyond any listing limit. */
  async countPendingCandidates(): Promise<number> {
    const { count, error } = await this.sb
      .from('fakturownia_attachment_candidates')
      .select('*', { count: 'exact', head: true })
      .eq('already_attached', false);
    if (error) throw error;
    return count ?? 0;
  }

  /** Record the currently-required files as already attached, WITHOUT uploading.
   *  Returns the number of ledger rows inserted. */
  async markAttached(invoiceIds: number[]): Promise<number> {
    // `sb` is an untyped SupabaseClient, so rpc().data is `any` — assert the SQL return type.
    const res = await this.sb.rpc('fakturownia_mark_attached', {
      p_invoice_ids: invoiceIds,
    });
    if (res.error) throw res.error;
    return (res.data as number | null) ?? 0;
  }
}
